import { useCallback, useEffect, useState } from 'react';
import { fetchActiveWbsSetupImportForCurrentUser } from '../logic/wbsSetupImportGuard';
import { subscribeRequestTrackingInvalidation } from '../logic/requestTrackingInvalidation';
import { runIfDocumentVisible } from '../logic/requestTrackingPoll';
import type { RequestTrackingItem } from '../logic/types';

export interface UseActiveWbsSetupImportResult {
  /** True when the current user has a queued/running MS_PROJECT_WBS_SETUP request. */
  importInProgress: boolean;
  activeRequest: RequestTrackingItem | null;
  loading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Loads active WBS setup import status.
 * Polls only while an import request is queued/running (not continuously idle).
 */
export function useActiveWbsSetupImport(
  pollIntervalMs = 10000,
): UseActiveWbsSetupImportResult {
  const [importInProgress, setImportInProgress] = useState(false);
  const [activeRequest, setActiveRequest] = useState<RequestTrackingItem | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const result = await fetchActiveWbsSetupImportForCurrentUser();
      setImportInProgress(result.blocked);
      setActiveRequest(result.activeRequest);
    } catch {
      setImportInProgress(false);
      setActiveRequest(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    return subscribeRequestTrackingInvalidation(() => {
      void refresh();
    });
  }, [refresh]);

  useEffect(() => {
    if (pollIntervalMs <= 0 || !importInProgress) {
      return;
    }
    const timer = window.setInterval(() => {
      runIfDocumentVisible(() => {
        void refresh();
      });
    }, pollIntervalMs);
    return () => window.clearInterval(timer);
  }, [pollIntervalMs, refresh, importInProgress]);

  return { importInProgress, activeRequest, loading, refresh };
}
